import { MesfModal } from "@dexteel/mesf-core";
import { Alert, Box, Button, CircularProgress } from "@mui/material";

import { useSectionSpecVersion } from "@/shared/hooks/useSectionSpec";
import type { SectionSpecDetail } from "@/shared/models/SectionSpec";
import { type SectionSpecEditorMode, SectionSpecForm } from "./SectionSpecForm";

interface SectionSpecEditorModalProps {
	mode: SectionSpecEditorMode;
	sectionId: number;
	assetId: number;
	sectionName: string;
	profileSpecId: number | null;
	sectionDetail: SectionSpecDetail;
	onClose: () => void;
	onSaved: (profileSpecId: number) => void;
	onError: (error: Error) => void;
}

export const SectionSpecEditorModal = ({
	mode,
	sectionId,
	assetId,
	sectionName,
	profileSpecId,
	sectionDetail,
	onClose,
	onSaved,
	onError,
}: SectionSpecEditorModalProps) => {
	const versionQuery = useSectionSpecVersion(
		mode === "create" ? null : profileSpecId,
	);
	const source =
		mode === "create"
			? sectionDetail
			: versionQuery.isFetchedAfterMount
				? versionQuery.data
				: undefined;

	return (
		<MesfModal
			open
			handleClose={onClose}
			maxWidth="xl"
			title={
				mode === "correction"
					? `Edit Published Revision - ${sectionName}`
					: mode === "draft"
						? `Edit Section Specification - ${sectionName}`
						: `Create Section Specification Draft - ${sectionName}`
			}
		>
			{versionQuery.isError ? (
				<MesfModal.Content dividers>
					<Alert
						severity="error"
						action={
							<Button
								color="inherit"
								size="small"
								disabled={versionQuery.isFetching}
								onClick={() => void versionQuery.refetch()}
							>
								Retry
							</Button>
						}
					>
						Unable to load the saved specification. {versionQuery.error.message}
					</Alert>
				</MesfModal.Content>
			) : source ? (
				<SectionSpecForm
					mode={mode}
					sectionId={sectionId}
					assetId={assetId}
					profileSpecId={profileSpecId}
					source={source}
					sectionDetail={sectionDetail}
					onClose={onClose}
					onSaved={onSaved}
					onError={onError}
				/>
			) : (
				<MesfModal.Content dividers>
					<Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
						<CircularProgress size={28} />
					</Box>
				</MesfModal.Content>
			)}
		</MesfModal>
	);
};
